import React, { useState, useEffect } from 'react'

import { Button, Icon, BookmarkAdd, BookmarkRemove, classnames } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { number, bool, string, oneOf } from 'prop-types'

import useGA4ItemTracking from 'Pods/ItemPage/Hooks/useGA4ItemTracking'
import useApiDelete from 'Pods/Shared/Hooks/useApiDelete'
import useApiGet from 'Pods/Shared/Hooks/useApiGet'
import useApiPost from 'Pods/Shared/Hooks/useApiPost'
import usePageConfig from 'Pods/Shared/Hooks/usePageConfig'
import useSentry from 'Pods/Shared/Hooks/useSentry'

function WishlistButton({
  productId,
  disabled = false,
  variant = 'default',
  customClass = '',
  label = null,
  savedLabel = null,
  size = null,
}) {
  const [wishListId, setWishListId] = useState(null)
  const [isInWishlist, setIsInWishlist] = useState(false)
  const [isInitialized, setIsInitialized] = useState(false)

  const { captureException } = useSentry()
  const pageConfig = usePageConfig()
  const { trackAddToWishlist } = useGA4ItemTracking()

  const {
    getData,
    isLoading: isLoadingGetData,
  } = useApiGet()

  const {
    postData,
    isLoading: isPostDataLoading,
  } = useApiPost()

  const {
    deleteData,
    isLoading: isDeleteDataLoading,
  } = useApiDelete()

  const isWishlistButtonLoading = isLoadingGetData || isPostDataLoading || isDeleteDataLoading

  const { isCurrentUser: isLoggedIn } = pageConfig || {}

  useEffect(() => {
    const { product } = pageConfig || {}
    if (!product) {
      return
    }

    setIsInWishlist(product.isInWishlist || false)
    setIsInitialized(true)
  }, [pageConfig])

  const handleError = (err) => {
    window.showNotification('Failed to interact with wishlist', {
      type: 'error',
      autohide: true,
    })
    captureException(err)
  }

  const handleAddToWishlistClick = () => {
    if (!isLoggedIn) {
      App.events.publish('app:login:open')
      return
    }

    const addItemToWishlist = (localWishListId) => {
      const addToWishlistData = {
        data: {
          type: 'wishListItems',
          attributes: {
            wish_list_id: localWishListId,
            item_id: productId,
          },
        },
      }
      postData({
        path: `/api/internal/wish-lists/${localWishListId}/items`,
        data: addToWishlistData,
        onSuccess: () => {
          window.showNotification(
            'Model was successfully added to wishlist!',
            {
              type: 'success',
              autohide: true,
            }
          )
          setIsInWishlist(true)
          trackAddToWishlist()
        },
        onError: handleError,
      })
    }

    getData({
      path: `/api/internal/items/${productId}/check-purchased`,
      onSuccess: (checkPurchasedResponse) => {
        if (checkPurchasedResponse.data.isPurchased) {
          window.showNotification('You have already purchased this item', {
            type: 'error',
            autohide: true,
          })
          return
        }

        if (wishListId) {
          addItemToWishlist(wishListId)
          return
        }

        getData({
          path: '/api/internal/wish-lists',
          onSuccess: (wishlistsResponse) => {
            if (wishlistsResponse?.data?.data?.id) {
              setWishListId(wishlistsResponse.data.data.id)
              addItemToWishlist(wishlistsResponse.data.data.id)
            } else {
              const newWishListData = {
                data: {
                  type: 'wishLists',
                  attributes: { title: 'Default' },
                },
              }
              postData({
                path: '/api/internal/wish-lists',
                data: newWishListData,
                onSuccess: (newWishlistResponse) => {
                  setWishListId(newWishlistResponse.data.data.id)
                  addItemToWishlist(newWishlistResponse.data.data.id)
                },
                onError: handleError,
              })
            }
          },
          onError: handleError,
        })
      },
      onError: handleError,
    })
  }

  const handleRemoveFromWishlistClick = () => {
    const removeItemFromWishlist = (localWishListId) => {
      const deleteItemFromWishlistData = { item_ids: productId }
      deleteData({
        path: `/api/internal/wish-lists/${localWishListId}/items/0`,
        data: deleteItemFromWishlistData,
        onSuccess: () => {
          window.showNotification(
            'Model was successfully removed from wishlist!',
            {
              type: 'success',
              autohide: true,
            }
          )
          setIsInWishlist(false)
        },
        onError: handleError,
      })
    }

    if (wishListId) {
      removeItemFromWishlist(wishListId)
      return
    }

    getData({
      path: '/api/internal/wish-lists',
      onSuccess: (wishlistsResponse) => {
        setWishListId(wishlistsResponse?.data?.data?.id)
        removeItemFromWishlist(wishlistsResponse?.data?.data?.id)
      },
      onError: handleError,
    })
  }

  const isCompact = variant === 'compact'
  const buttonType = isCompact ? 'ghost' : 'secondary'
  const buttonSize = size || (isCompact ? 'medium' : 'large')
  const defaultClass = !isCompact && 'pricing-area__actions-button pricing-area__actions-left-button'
  const disabledClass = !isLoggedIn && !isCompact && 'pricing-area__actions-left-button--is-disabled'

  const addLabel = label || 'Add to wishlist'
  const removeLabel = savedLabel || 'Remove from wishlist'
  const buttonLabel = isInWishlist ? removeLabel : addLabel

  return (
    <Button
      type={buttonType}
      customClass={classnames(defaultClass, disabledClass, customClass)}
      size={buttonSize}
      icon={<Icon svgComponent={isInWishlist ? BookmarkRemove : BookmarkAdd} />}
      onClick={isInWishlist ? handleRemoveFromWishlistClick : handleAddToWishlistClick}
      disabled={disabled || !isInitialized || isWishlistButtonLoading}
      aria-label={isInWishlist ? 'Remove from wishlist' : 'Save to wishlist'}
    >
      {buttonLabel}
    </Button>
  )
}

WishlistButton.propTypes = {
  productId: number.isRequired,
  disabled: bool,
  variant: oneOf(['default', 'compact']),
  customClass: string,
  label: string,
  savedLabel: string,
  size: oneOf(['small', 'medium', 'large']),
}

export default WishlistButton
